import { MobiSimRegistrationRequestEntity } from "features/mobiSimRegistration/domain/entities/MobiSimRegistrationRequestEntity";
import { ServiceEnum } from "utils/enums/ServiceEnum";

export interface SelectOption {
  label?: string;
  value?: string;
}

export interface SearchFormValues {
  created?: Array<Record<string, unknown>>;
  status?: number | string;
  code?: string;
  transRef?: string;
  partner?: string;
  platform?: string;
  user?: string[];
  store?: string;
  type?: number | string;
}

export interface SearchContext {
  staffOptions: SelectOption[];
  userOptions: SelectOption[];
  selectedProviderCode?: string;
  selectedPartnerType: number;
  isStore: boolean;
  storeCode?: string;
  limit: number;
}

export class MobiSimRegistrationQuery {
  static resolveUserRef(
    rawUserRef: string[] | undefined,
    staffOptions: SelectOption[],
    userOptions: SelectOption[],
    selectedPartnerType: number,
    isStore: boolean,
  ): string[] | undefined {
    if (!rawUserRef || rawUserRef.length === 0) return undefined;
    if (isStore) {
      return rawUserRef.filter((ele) =>
        staffOptions?.some((child) => child?.value === ele),
      );
    }
    if (selectedPartnerType === 2 || (selectedPartnerType === 1 && !isStore)) {
      return rawUserRef.filter((ele) =>
        userOptions?.some((child) => child?.value === ele),
      );
    }
    return undefined;
  }

  static sanitize<T extends Record<string, unknown>>(obj: T): T {
    const result = { ...obj };
    Object.keys(result).forEach((key) => {
      const value = result[key];
      if (
        value === undefined ||
        value === null ||
        (typeof value === "string" && value.trim() === "") ||
        (Array.isArray(value) &&
          (value.length === 0 || value.every((v) => !v || v === "")))
      ) {
        delete result[key];
      }
    });
    return result as T;
  }

  static buildSearchQuery(
    formValues: SearchFormValues,
    context: SearchContext,
    dateFrom: string,
    dateTo: string,
  ): MobiSimRegistrationRequestEntity {
    const finalUserRef = this.resolveUserRef(
      formValues.user,
      context.staffOptions,
      context.userOptions,
      context.selectedPartnerType,
      context.isStore,
    );

    const raw = new MobiSimRegistrationRequestEntity({
      fromDate: dateFrom,
      toDate: dateTo,
      status:
        formValues.status !== undefined ? String(formValues.status) : undefined,
      code: formValues.code,
      transRef: formValues.transRef,
      partner: formValues.partner,
      platform: formValues.platform,
      store: context.storeCode ?? formValues.store,
      user: finalUserRef?.[0],
      type: formValues.type !== undefined ? String(formValues.type) : undefined,
      page: 0,
      limit: context.limit ?? 10,
      service: ServiceEnum.SIM_ECOMMERCE,
    });

    return this.sanitize(
      raw as unknown as Record<string, unknown>,
    ) as unknown as MobiSimRegistrationRequestEntity;
  }

  static buildExportQuery(
    formValues: SearchFormValues,
    context: SearchContext,
    dateFrom: string,
    dateTo: string,
  ): MobiSimRegistrationRequestEntity {
    const params = this.buildSearchQuery(formValues, context, dateFrom, dateTo);
    delete params.page;
    delete params.limit;
    return params;
  }

  static buildTableChangeQuery(
    prevParams: MobiSimRegistrationRequestEntity,
    formValues: Partial<SearchFormValues>,
    page: number,
    limit: number,
    dateFrom?: string,
    dateTo?: string,
  ): MobiSimRegistrationRequestEntity {
    const status =
      formValues.status !== undefined
        ? typeof formValues.status === "string"
          ? Number(formValues.status)
          : formValues.status
        : prevParams.status;
    const raw = new MobiSimRegistrationRequestEntity({
      ...prevParams,
      status: status !== undefined ? String(status) : undefined,
      type: formValues.type !== undefined ? String(formValues.type) : prevParams.type,
      code: formValues.code ?? prevParams.code,
      transRef: formValues.transRef ?? prevParams.transRef,
      partner: formValues.partner ?? prevParams.partner,
      platform: formValues.platform ?? prevParams.platform,
      store: formValues.store ?? prevParams.store,
      user: formValues.user?.[0] ?? prevParams.user,
      fromDate: dateFrom || prevParams.fromDate,
      toDate: dateTo || prevParams.toDate,
      page,
      limit,
    });

    return this.sanitize(
      raw as unknown as Record<string, unknown>,
    ) as unknown as MobiSimRegistrationRequestEntity;
  }

  static buildRedoQuery(
    fromDate: string,
    toDate: string,
  ): MobiSimRegistrationRequestEntity {
    return new MobiSimRegistrationRequestEntity({
      page: 0,
      limit: 10,
      fromDate,
      toDate,
      service: ServiceEnum.SIM_ECOMMERCE,
    });
  }
}
